import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Shield, WarningCircle, Clock, Plus, Sparkle, Globe,
  ChartBar, Heartbeat, Lightning, Play, ListBullets,
  File, SquaresFour, UsersThree, TrendUp, CaretLeft, CaretRight
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const ICON_MAP = {
  shield: Shield,
  warning: WarningCircle,
  clock: Clock,
  plus: Plus,
  sparkle: Sparkle,
  globe: Globe,
  chart: ChartBar,
  heartbeat: Heartbeat,
  lightning: Lightning,
  play: Play,
  list: ListBullets,
  file: File,
  grid: SquaresFour,
  users: UsersThree,
  trending: TrendUp,
};

const SuggestionChips = ({ onCommand, currentRoute, workflowContext }) => {
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const API = BACKEND_URL;

  const fetchChips = useCallback(async () => {
    // If workflow context is active, show worker-type-specific chips
    if (workflowContext?.automationType) {
      const WORKFLOW_CHIPS = {
        file_analysis: [
          { id: 'wf_add_input', label: 'Add input source', icon: 'plus', command: 'Add a new input step to this File worker' },
          { id: 'wf_file_types', label: 'Configure file types', icon: 'file', command: 'Help me configure accepted file types' },
          { id: 'wf_add_export', label: 'Add export output', icon: 'lightning', command: 'Add an export output step' },
          { id: 'wf_analysis_tips', label: 'Analysis tips', icon: 'sparkle', command: 'Best practices for file analysis workers' },
        ],
        chat: [
          { id: 'wf_greeting', label: 'Set greeting', icon: 'sparkle', command: 'Help me write a greeting message for this Chat worker' },
          { id: 'wf_add_fallback', label: 'Add fallback', icon: 'plus', command: 'Add a fallback response step' },
          { id: 'wf_context', label: 'Set context', icon: 'globe', command: 'Help me configure the conversation context' },
        ],
        voice: [
          { id: 'wf_voice_config', label: 'Configure voice', icon: 'sparkle', command: 'Help me configure voice settings' },
          { id: 'wf_call_handling', label: 'Call handling', icon: 'lightning', command: 'Set up call handling rules' },
          { id: 'wf_transcript', label: 'Add transcript', icon: 'file', command: 'Add a transcription output step' },
        ],
        operator: [
          { id: 'wf_tools', label: 'Add tool access', icon: 'lightning', command: 'Configure tool access for this Operator' },
          { id: 'wf_schedule', label: 'Set schedule', icon: 'clock', command: 'Schedule this Operator to run periodically' },
          { id: 'wf_webhook', label: 'Add webhook', icon: 'globe', command: 'Add a webhook output' },
        ],
      };
      // For event workers, use the specific template type or fallback
      const eventChips = [
        { id: 'wf_add_trigger', label: 'Add trigger', icon: 'lightning', command: 'Add a new trigger to this workflow' },
        { id: 'wf_add_condition', label: 'Add condition', icon: 'sparkle', command: 'Add a condition step' },
        { id: 'wf_connect', label: 'Connect app', icon: 'globe', command: 'Connect an app integration' },
        { id: 'wf_add_output', label: 'Add output', icon: 'plus', command: 'Add an output step' },
      ];
      const wt = workflowContext.automationType;
      const wfChips = WORKFLOW_CHIPS[wt] || eventChips;
      setChips(wfChips);
      return;
    }

    if (!API) return;
    setLoading(true);
    try {
      const route = currentRoute || window.location.pathname;
      // Fetch both page suggestions and system context in parallel
      const [suggestRes, contextRes] = await Promise.allSettled([
        fetch(`${API}/api/command/suggestions?route=${encodeURIComponent(route)}`, { credentials: 'include' }),
        fetch(`${API}/api/command/system-context`, { credentials: 'include' }),
      ]);

      let pageChips = [];
      let systemChips = [];

      if (suggestRes.status === 'fulfilled' && suggestRes.value.ok) {
        const data = await suggestRes.value.json();
        pageChips = (data.chips || []).filter(c => c.category !== 'system');
      }

      if (contextRes.status === 'fulfilled' && contextRes.value.ok) {
        const data = await contextRes.value.json();
        systemChips = data.chips || [];
      }

      // Merge: system chips first (higher priority), then page chips
      const merged = [...systemChips, ...pageChips];
      // Deduplicate by id
      const seen = new Set();
      const deduped = merged.filter(c => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      setChips(deduped.slice(0, 8));
    } catch (e) {
      // Silent fail — chips are non-critical
    } finally {
      setLoading(false);
    }
  }, [API, currentRoute, workflowContext]);

  useEffect(() => {
    fetchChips();
  }, [fetchChips]);

  // Refresh chips on route change
  useEffect(() => {
    fetchChips();
  }, [currentRoute]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    return () => { if (el) el.removeEventListener('scroll', checkScroll); };
  }, [chips, checkScroll]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * 180, behavior: 'smooth' });
  };

  if (chips.length === 0) return null;

  return (
    <div className="px-4 pb-1 shrink-0 md:pl-5 md:pr-4 md:max-w-[520px] md:ml-0 relative" data-testid="suggestion-chips">
      {/* Scroll left arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll(-1)}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full bg-[var(--surface-3)] flex items-center justify-center text-[var(--text-muted)] hover:text-white transition-colors"
          data-testid="suggestion-scroll-left"
        >
          <CaretLeft size={10} weight="bold" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex gap-1.5 overflow-x-auto scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {chips.map(chip => {
          const Icon = ICON_MAP[chip.icon] || Lightning;
          const isSystem = chip.category === 'system';
          return (
            <button
              key={chip.id}
              onClick={() => onCommand(chip.command)}
              className={`flex items-center gap-1.5 px-3 py-1.5 md:px-2.5 md:py-1 rounded-full border text-xs md:text-[11px] whitespace-nowrap shrink-0 transition-all ${
                isSystem
                  ? 'border-amber-500/25 bg-amber-500/8 text-amber-300 hover:bg-amber-500/15 hover:border-amber-500/40'
                  : 'border-[var(--border-subtle)] bg-[var(--surface-1)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--brand-primary)] hover:bg-[rgba(99,102,241,0.06)]'
              }`}
              data-testid={`suggestion-chip-${chip.id}`}
            >
              <Icon size={11} weight="duotone" />
              {chip.label}
            </button>
          );
        })}
      </div>

      {/* Scroll right arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll(1)}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full bg-[var(--surface-3)] flex items-center justify-center text-[var(--text-muted)] hover:text-white transition-colors"
          data-testid="suggestion-scroll-right"
        >
          <CaretRight size={10} weight="bold" />
        </button>
      )}
    </div>
  );
};

export default SuggestionChips;
